import React, { useState, useRef, useEffect } from "react";
import { LoadingButton } from "@/components/shared/LoadingSystem";
import "./ImageUploader.css";
import { sileo } from "sileo";
import fileService from "@shared/api/filesService";
import { isAuthenticated } from "../../../../utils/authStorage";

const ImageUploader = ({
  onImageUpload,
  idReporte,
  idItem,
  idRespuestaImagen,
  idMaquina = null,
}) => {
  const [imageData, setImageData] = useState({ file: null, previewUrl: null });
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [hasCameraSupport, setHasCameraSupport] = useState(true); // Cambiado a true por defecto
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Check if device has camera support - ahora más simple y siempre intenta detectar una cámara
  useEffect(() => {
    // Solo verificamos si el navegador soporta mediaDevices API
    const hasMediaDevices = !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    );

    if (hasMediaDevices) {
      // Si el navegador soporta la API, asumimos que puede tener una cámara
      setHasCameraSupport(true);
    } else {
      console.log("Este navegador no soporta acceso a la cámara");
      setHasCameraSupport(false);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    validateAndSetImage(file);
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    validateAndSetImage(file);
  };

  const validateAndSetImage = (file) => {
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith("image/")) {
      sileo.error({
        title: "Por favor seleccione un archivo de imagen válido",
      });
      return;
    }

    // Validar tamaño máximo (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      sileo.error({
        title: "La imagen es demasiado grande. El tamaño máximo es 5MB",
      });
      return;
    }

    setImageData({ file, previewUrl: URL.createObjectURL(file) });
  };

  const openCamera = () => {
    // Método mejorado para abrir la cámara en cualquier dispositivo
    if (cameraInputRef.current) {
      // Para iOS queremos el atributo capture sin valor
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        cameraInputRef.current.setAttribute("capture", "camera");
      } else {
        // En otros dispositivos podemos especificar la cámara trasera
        cameraInputRef.current.setAttribute("capture", "environment");
      }

      cameraInputRef.current.click();
    }
  };

  const uploadImage = async () => {
    if (!imageData.file) {
      sileo.error({ title: "Por favor seleccione una imagen primero" });
      return;
    }

    setUploading(true);
    try {
      if (!isAuthenticated()) {
        sileo.error({ title: "No se encontró el token de autenticación" });
        return;
      }

      // Crear nomenclatura de archivo: Fecha_idreporte_iditem_idrespuesta_idmaquina_idunico
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0].replace(/-/g, "");
      const uniqueId = Math.random().toString(36).substring(2, 10);
      const maquinaStr = idMaquina ? `_${idMaquina}` : "";
      const fileExt = imageData.file.name.split(".").pop();

      // Renombrar el archivo
      const fileName = `${dateStr}_${idReporte}_${idItem}_${idRespuestaImagen}${maquinaStr}_${uniqueId}.${fileExt}`;

      // Crear un nuevo objeto File con el nombre personalizado
      const renamedFile = new File([imageData.file], fileName, {
        type: imageData.file.type,
      });

      // Usar el servicio de archivos para la carga
      const response = await fileService.uploadFile(
        renamedFile,
        "ImagenesEvidencias",
      );

      // Si se sube con éxito, obtener la ruta del archivo
      if (response && response.key) {
        const imagePath = response.key;

        // En lugar de enviar un objeto, pasar los valores separados
        onImageUpload(imagePath, description);

        // Limpiar el formulario
        setImageData({ file: null, previewUrl: null });
        setDescription("");

        sileo.success({ title: "Imagen cargada exitosamente" });
      } else {
        console.error("Respuesta del servidor:", response);
        throw new Error(
          "No se recibió una respuesta válida al subir el archivo",
        );
      }
    } catch (error) {
      console.error("Error al subir la imagen:", error);
      sileo.error({
        title:
          error.response?.data?.message ||
          "Error al subir la imagen. Intente nuevamente.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImageData({ file: null, previewUrl: null });
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  return (
    <div className="image-uploader">
      {imageData.previewUrl ? (
        <div className="image-preview-container">
          <img src={imageData.previewUrl} alt="Vista previa" className="image-preview" />
          <button
            type="button"
            className="remove-image-btn"
            onClick={handleRemoveImage}
            aria-label="Eliminar"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      ) : (
        <div className="image-upload-buttons">
          <button
            type="button"
            className="upload-btn"
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
            title="Seleccionar imagen"
          >
            <i className="fa-solid fa-image"></i>
            <span>Galería</span>
          </button>

          {/* Mostramos el botón de cámara para todos los dispositivos compatibles */}
          {hasCameraSupport && (
            <button
              type="button"
              className="camera-btn"
              onClick={openCamera}
              disabled={uploading}
              title="Tomar foto"
            >
              <i className="fa-solid fa-camera"></i>
              <span>Cámara</span>
            </button>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: "none" }}
          />

          <input
            type="file"
            accept="image/*"
            onChange={handleCameraCapture}
            ref={cameraInputRef}
            style={{ display: "none" }}
          />
        </div>
      )}

      {imageData.previewUrl && (
        <div className="image-details">
          <textarea
            className="image-description"
            placeholder="Descripción breve..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={100}
          />

          <LoadingButton
            onClick={uploadImage}
            loading={uploading}
            loadingText="Subiendo..."
            className="upload-image-btn"
            title="Guardar imagen"
          >
            <i className="fa-solid fa-cloud-arrow-up"></i>
            <span>Guardar</span>
          </LoadingButton>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
